import { ChangeCashMode } from './model';
import type { FC } from 'react';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Tooltip } from '@mui/material';
import { Button, Iconify, Stack, Typography } from '@pflegenavi/web-components';
import { LetterChip } from './LetterChip';
import { useFeatureFlagEnabled } from '@pflegenavi/frontend/feature-flags';
import { FeatureFlag } from '@pflegenavi/shared/api';
import { useCashManagementCapabilities } from './useCashManagementCapabilities';

interface ChangeCashFormStep1Props {
  nextStep: () => void;
  setMode: (mode: ChangeCashMode) => void;
  withdrawDisabled?: boolean;
  depositDisabled?: boolean;
  disabledTooltipTitle?: string;
}

export const ChangeCashFormStep1: FC<ChangeCashFormStep1Props> = ({
  nextStep,
  setMode,
  withdrawDisabled,
  depositDisabled,
  disabledTooltipTitle,
}) => {
  const { t } = useTranslation();
  const hideDeposit = useFeatureFlagEnabled(
    FeatureFlag.HideCashManagementDeposit
  );
  const { receiptManagementEnabled, variant } = useCashManagementCapabilities();

  return (
    <Stack
      direction="column"
      sx={{
        gap: 1,
        paddingY: 1,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: 'center',
        }}
      >
        <LetterChip>A</LetterChip>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
          }}
        >
          {hideDeposit
            ? t('cashManagement.withdraw')
            : `${t('cashManagement.deposit')} / ${t(
                'cashManagement.withdraw'
              )}`}
        </Typography>
      </Stack>

      <Typography variant="body2">
        <Trans
          i18nKey={`cashManagement.withdraw-explanation-${variant}`}
          components={{ bold: <strong /> }}
        />
      </Typography>

      {!hideDeposit && (
        <Typography variant="body2">
          <Trans
            i18nKey="cashManagement.deposit-explanation"
            components={{ bold: <strong /> }}
          />
          {receiptManagementEnabled && (
            <>
              {' '}
              <Trans
                i18nKey="cashManagement.deposit-explanation-receipt"
                components={{ bold: <strong /> }}
              />
            </>
          )}
        </Typography>
      )}

      <Stack
        direction="row"
        sx={{
          gap: 1,
          justifyContent: 'end',
        }}
      >
        <Tooltip title={withdrawDisabled ? disabledTooltipTitle ?? '' : ''}>
          <span>
            <Button
              name={'changeCashWithdraw'}
              sx={{ width: 140 }}
              variant="contained"
              color="error"
              disabled={withdrawDisabled}
              endIcon={<Iconify icon="eva:trending-down-fill" />}
              onClick={() => {
                setMode(ChangeCashMode.Withdraw);
                nextStep();
              }}
            >
              {t('cashManagement.withdraw')}
            </Button>
          </span>
        </Tooltip>

        {!hideDeposit && (
          <Tooltip title={depositDisabled ? disabledTooltipTitle ?? '' : ''}>
            <span>
              <Button
                name={'changeCashDeposit'}
                sx={{ width: 140 }}
                variant="contained"
                disabled={depositDisabled}
                endIcon={<Iconify icon="eva:trending-up-fill" />}
                onClick={() => {
                  setMode(ChangeCashMode.Deposit);
                  nextStep();
                }}
              >
                {t('cashManagement.deposit')}
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>
    </Stack>
  );
};
